import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from './Layout';
import MetaTags from './meta-tags';
import { useLanguage } from '../contexts/language-context';
import { useToast } from '../contexts/toast-context';
import { submitContactForm } from '../services/contact-service';
import { CONTACT_EMAIL } from '../config/app-config';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function ContactPage() {
  const { t } = useLanguage();
  const { showSuccess, showError, showInfo } = useToast();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const raw = searchParams.get('parama');
    if (!raw) return;
    const n = parseFloat(String(raw).replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) return;
    const display = String(raw).replace(',', '.');
    setMessage((prev) => {
      if (prev.trim()) return prev;
      return t('donateMessagePrefill').replace('{{amount}}', display);
    });
  }, [searchParams, t]);

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName) {
      showError(t('contactNameRequired'));
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      showError(t('contactEmailInvalid'));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitContactForm({
        name: trimmedName,
        email: trimmedEmail,
        message: trimmedMessage,
      });
      if (result.ok) {
        showSuccess(t('contactSuccess'));
        setName('');
        setEmail('');
        setMessage('');
      } else if (result.error === 'not_configured') {
        showInfo(t('contactNotConfigured'));
      } else {
        showError(t('contactError'));
      }
    } catch {
      showError(t('contactError'));
    } finally {
      setIsSubmitting(false);
    }
  }

  const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('FilmųDB užklausa')}`;

  return (
    <Layout>
      <MetaTags
        title={`FilmųDB — ${t('contactTitle')}`}
        description={t('contactIntro')}
        url="https://filmudb.lt/kontaktai"
        keywords="kontaktai, FilmųDB, pagalba, contact"
      />
      <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="mb-8 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">{t('contactTitle')}</h1>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed">{t('contactIntro')}</p>
          </div>

          <div className="bg-dark-800/80 backdrop-blur-sm rounded-2xl border border-dark-700 p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-gray-300 mb-1.5">
                  {t('contactNameLabel')}
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('contactNamePlaceholder')}
                  className="w-full rounded-xl bg-dark-900/80 border border-dark-600 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/60 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-gray-300 mb-1.5">
                  {t('contactEmailLabel')}
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl bg-dark-900/80 border border-dark-600 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/60 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-gray-300 mb-1.5">
                  {t('contactMessageLabel')}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('contactMessagePlaceholder')}
                  className="w-full rounded-xl bg-dark-900/80 border border-dark-600 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/60 focus:border-primary-500 resize-y min-h-[120px]"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto rounded-xl bg-primary-600 hover:bg-primary-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-8 py-3 transition-colors"
              >
                {isSubmitting ? t('contactSending') : t('contactSubmit')}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-dark-700">
              <p className="text-sm text-gray-500 mb-2">{t('contactFallback')}</p>
              <a
                href={mailtoHref}
                className="text-primary-400 hover:text-primary-300 text-sm font-medium break-all"
              >
                {CONTACT_EMAIL}
              </a>
            </div>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-gray-500 hover:text-gray-300 transition-colors">
                ← {t('goBack')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
